import {
  DEFAULT_SETTINGS,
  STORAGE_SCHEMA_VERSION,
  type Settings,
} from '@core/settings/schema.ts';
import { applyMigrations } from '@core/settings/migrations.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import { migrateLearningTargetLanguage } from '../../lib/azureLanguage.ts';

export const SETTINGS_STORAGE_KEY = 'rikosub:settings';

const KNOWN_KEYS = new Set(Object.keys(DEFAULT_SETTINGS) as Array<keyof Settings>);

function createMemoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear() {
      map.clear();
    },
    getItem(key: string) {
      return map.has(key) ? map.get(key)! : null;
    },
    key(index: number) {
      return [...map.keys()][index] ?? null;
    },
    removeItem(key: string) {
      map.delete(key);
    },
    setItem(key: string, value: string) {
      map.set(key, String(value));
    },
  };
}

function resolveDefaultStorage(): Storage {
  try {
    if (typeof localStorage !== 'undefined' && localStorage) {
      const probe = '__rikosub_probe__';
      localStorage.setItem(probe, '1');
      localStorage.removeItem(probe);
      return localStorage;
    }
  } catch {
    // happy-dom / private mode
  }
  return createMemoryStorage();
}

export type LocalStorageSettingsStoreOptions = {
  storage?: Storage;
  storageKey?: string;
  /** Window for `storage` events (multi-tab). Pass `null` to disable. */
  host?: (Window & typeof globalThis) | null;
};

function pickKnown(raw: Record<string, unknown>): Partial<Settings> {
  const out: Partial<Settings> = {};
  for (const key of KNOWN_KEYS) {
    if (Object.prototype.hasOwnProperty.call(raw, key)) {
      (out as Record<string, unknown>)[key] = raw[key as string];
    }
  }
  return out;
}

function hydrate(raw: unknown): Settings {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_SETTINGS };
  }
  const picked = pickKnown(raw as Record<string, unknown>);
  // Extension applyMigrations: missing subtitleOptimize → true for upgrades;
  // missing/invalid subtitleOptimizeModel → auto; fresh DEFAULT_SETTINGS keeps
  // subtitleOptimize false and subtitleOptimizeModel auto for new installs.
  const migrated = applyMigrations({
    ...picked,
    version: typeof picked.version === 'string' ? picked.version : undefined,
  });
  const merged: Settings = {
    ...DEFAULT_SETTINGS,
    ...migrated,
    version: STORAGE_SCHEMA_VERSION,
    shortcutBindings: {
      ...DEFAULT_SETTINGS.shortcutBindings,
      ...(migrated.shortcutBindings ?? {}),
    },
  };
  // Learning GPT uses Azure codes (zh-Hans / zh-Hant); migrate legacy zh-CN / zh-TW.
  if (merged.learningModeTargetLanguage) {
    const lang = migrateLearningTargetLanguage(merged.learningModeTargetLanguage);
    if (lang) merged.learningModeTargetLanguage = lang;
  }
  return merged;
}

export class LocalStorageSettingsStore implements SettingsStore {
  private snapshot: Settings;
  /**
   * Extension page-runtime style overlay (language→custom style priorities).
   * Merged into getSnapshot / subscriber payloads; never written to localStorage.
   */
  private runtimeOverlay: Partial<Settings> = {};
  private readonly storage: Storage;
  private readonly storageKey: string;
  private readonly host: (Window & typeof globalThis) | null;
  private readonly subscribers = new Set<(settings: Settings) => void>();
  private readonly onStorage = (event: StorageEvent): void => {
    if (event.key !== this.storageKey) return;
    this.snapshot = this.readFromStorage();
    this.notify();
  };

  constructor(options: LocalStorageSettingsStoreOptions = {}) {
    this.storage = options.storage ?? resolveDefaultStorage();
    this.storageKey = options.storageKey ?? SETTINGS_STORAGE_KEY;
    if (options.host === null) {
      this.host = null;
    } else {
      this.host = options.host ?? (typeof window !== 'undefined' ? window : null);
    }
    this.snapshot = this.readFromStorage();
    if (!this.storage.getItem(this.storageKey)) {
      this.persist(this.snapshot);
    }
    this.host?.addEventListener('storage', this.onStorage);
  }

  private readFromStorage(): Settings {
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) return { ...DEFAULT_SETTINGS };
      return hydrate(JSON.parse(raw) as unknown);
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  }

  private persist(settings: Settings): void {
    this.storage.setItem(this.storageKey, JSON.stringify(settings));
  }

  private effective(): Settings {
    if (Object.keys(this.runtimeOverlay).length === 0) return this.snapshot;
    return { ...this.snapshot, ...this.runtimeOverlay };
  }

  private notify(): void {
    const next = this.effective();
    for (const cb of [...this.subscribers]) cb(next);
  }

  /**
   * Runtime-only style patch (extension Object.assign on page storage).
   * Does not touch localStorage.
   */
  applyRuntimeOverlay(patch: Partial<Settings>): void {
    const cleaned = pickKnown(patch as Record<string, unknown>);
    if (Object.keys(cleaned).length === 0) return;
    this.runtimeOverlay = { ...this.runtimeOverlay, ...cleaned };
    this.notify();
  }

  /** Persisted settings only (tests / diagnostics). */
  getPersistedSnapshot(): Settings {
    return this.snapshot;
  }

  async getAll(): Promise<Settings> {
    this.snapshot = this.readFromStorage();
    return this.effective();
  }

  getSnapshot(): Settings {
    return this.effective();
  }

  async set(patch: Partial<Settings>): Promise<void> {
    const cleaned = pickKnown(patch as Record<string, unknown>);
    // User/persisted writes win over language-bound runtime overlay for those keys.
    for (const key of Object.keys(cleaned) as Array<keyof Settings>) {
      delete this.runtimeOverlay[key];
    }
    this.snapshot = {
      ...this.snapshot,
      ...cleaned,
      version: STORAGE_SCHEMA_VERSION,
    };
    this.persist(this.snapshot);
    this.notify();
  }

  subscribe(cb: (settings: Settings) => void): () => void {
    this.subscribers.add(cb);
    return () => {
      this.subscribers.delete(cb);
    };
  }

  dispose(): void {
    this.host?.removeEventListener('storage', this.onStorage);
    this.subscribers.clear();
  }
}
